import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import config from '../../config';
import facebookImage from '../../assets/freedomlySocialShare.png';
import twitterImage from '../../assets/freedomlySocialShare.png';
import {
  LuPenTool,
  LuPalette,
  LuLayoutTemplate,
  LuMegaphone,
  LuVideo,
  LuCode2,
  LuPenLine,
} from 'react-icons/lu';
import { BiCategory } from 'react-icons/bi';

import {
  Page,
  NamedLink,
  SectionLocationsCategoriesPage,
  ContactPreFooter,
  Footer,
} from '../../components';

import css from './CategoriesPage.css';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import { pickSearchParamsOnly } from '../SearchPage/SearchPage.helpers';
import { parse } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import classNames from 'classnames';

const siteTitle = config.siteTitle;
const schemaImage = `${config.canonicalRootURL}${facebookImage}`;

const CATEGORY_ICONS = {
  content_creating: LuPenTool,
  graphic_design: LuPalette,
  ecommerce_n_websites: LuLayoutTemplate,
  digital_marketing: LuMegaphone,
  photography_n_video: LuVideo,
  programming_n_web_development: LuCode2,
  writing_and_translation: LuPenLine,
  other_categories: BiCategory,
};

/* BoxIcons are solid glyphs painted with fill, unlike the stroke-based Lucide
   icons, so they need the opposite fill/stroke treatment. */
const FILLED_ICONS = [BiCategory];

const CategoryCard = props => {
  const { category, categoriesAll } = props;
  const Icon = CATEGORY_ICONS[category.key] || BiCategory;
  const childCategories = categoriesAll.filter(child => child.parent === category.key);
  const iconClasses = classNames(css.categoryCardIcon, {
    [css.categoryCardIconFilled]: FILLED_ICONS.includes(Icon),
  });

  return (
    <div className={css.categoryCard}>
      <NamedLink name={category.name} className={css.categoryCardHeader}>
        <Icon className={iconClasses} />
        <h3 className={css.categoryCardTitle}>{category.label}</h3>
      </NamedLink>
      <div className={css.categoryCardLinks}>
        {childCategories.map(child => (
          <NamedLink key={child.key} name={child.name} className={css.categoryLink}>
            {child.label}
          </NamedLink>
        ))}
      </div>
    </div>
  );
};

const CategoriesPageComponent = props => {
  const isMobileModalOpen = false;

  const { intl, filterConfig, sortConfig, location } = props;
  const { mapSearch, page, ...searchInURL } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const schemaTitle = intl.formatMessage({ id: 'CategoriesPage.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage({ id: 'CategoriesPage.schemaDescription' });

  // urlQueryParams doesn't contain page specific url params
  // like mapSearch, page or origin (origin depends on config.sortSearchByDistance)
  const urlQueryParams = pickSearchParamsOnly(searchInURL, filterConfig, sortConfig);

  const topbarClasses = isMobileModalOpen
    ? classNames(css.topbarBehindModal, css.topbar)
    : css.topbar;

  const categoriesAll = filterConfig[1]?.config.options;

  const parentCategories = categoriesAll
    ?.filter(x => x.parent === '')
    .sort((x, y) => x.parentOrder - y.parentOrder);

  return (
    <Page
      className={css.root}
      scrollingDisabled={false}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[
        { url: `${config.canonicalRootURL}${facebookImage}`, width: 1200, height: 630 },
      ]}
      twitterImages={[
        { url: `${config.canonicalRootURL}${twitterImage}`, width: 600, height: 314 },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <TopbarContainer
        className={topbarClasses}
        currentSearchParams={urlQueryParams}
        currentPage="CategoriesPage"
      />
      <div className={css.container}>
        <div className={css.content}>
          <div className={css.hero}>
            <h1 className={css.heroTitle}>
              <FormattedMessage id="CategoriesPage.heroTitle" />
            </h1>
            <p className={css.heroSubtitle}>
              <FormattedMessage id="CategoriesPage.heroSubtitle" />
            </p>
          </div>
          <div className={css.featuredSection}>
            <SectionLocationsCategoriesPage categoryPage="true" />
          </div>
          <div className={css.allCategoriesSection}>
            <h2 className={css.sectionTitle}>
              <FormattedMessage id="SectionLocations.allCategoriesLabel" />
            </h2>
            <div className={css.categoryGrid}>
              {parentCategories.map(category => (
                <CategoryCard
                  key={category.key}
                  category={category}
                  categoriesAll={categoriesAll}
                />
              ))}
            </div>
          </div>
        </div>
        <ContactPreFooter />
        <Footer />
      </div>
    </Page>
  );
};

CategoriesPageComponent.defaultProps = {
  filterConfig: config.custom.filters,
  sortConfig: config.custom.sortConfig,
};

CategoriesPageComponent.propTypes = {
  filterConfig: propTypes.filterConfig,
  sortConfig: propTypes.sortConfig,
};

const CategoriesPage = compose(withRouter, connect(), injectIntl)(CategoriesPageComponent);

export default CategoriesPage;
